import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import {
  Modal,
  Stack,
  Group,
  Box,
  Text,
  Textarea,
  Button,
  Alert,
  Divider,
  UnstyledButton,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconCircle, IconCircleCheck, IconPhotoOff } from "@tabler/icons-react";
import { useMessaging } from "../../context/MessagingContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { notifySuccess } from "../../utils/notify";
import { computeSmsSegments } from "../../utils/smsSegments";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import styles from "./ScheduleSendModal.module.css";

/** Quick-pick options for when to send. */
type WhenChoice = "morning" | "afternoon" | "custom";

interface ScheduleSendModalProps {
  opened: boolean;
  onClose: () => void;
  conversationId: string;
  /** Salon timezone — presets and the custom picker are wall-clock in this tz. */
  timezone: string;
  /** Message text carried in from the composer. */
  initialBody: string;
  /** True when the composer had a staged image (dropped from this text-only send). */
  mediaStripped: boolean;
  /** Fired after a successful schedule (parent clears the composer text). */
  onScheduled: () => void;
}

const WALL = "YYYY-MM-DD HH:mm:ss";
const MORNING_HOUR = 9; // 9:00 AM
const AFTERNOON_HOUR = 14; // 2:00 PM

/** A selectable "when to send" row — radio indicator, label + caption, value on
 *  the right. Mirrors checkout's RadioRingRow using the shared ring tokens. */
const PresetRow: React.FC<{
  selected: boolean;
  onSelect: () => void;
  title: string;
  value: string;
  caption?: string;
}> = ({ selected, onSelect, title, value, caption }) => (
  <UnstyledButton
    className={
      selected
        ? `${styles.presetRow} ${styles.presetRowSelected}`
        : styles.presetRow
    }
    onClick={onSelect}
    role="radio"
    aria-checked={selected}
  >
    <Box className={styles.presetRadio}>
      {selected ? (
        <IconCircleCheck size={20} color="var(--brand-teal)" />
      ) : (
        <IconCircle size={20} color="#c7d2d4" />
      )}
    </Box>
    <Box className={styles.presetBody}>
      <Text className={styles.presetTitle}>{title}</Text>
      {caption && <Text className={styles.presetCaption}>{caption}</Text>}
    </Box>
    <Text className={styles.presetValue}>{value}</Text>
  </UnstyledButton>
);

/**
 * Compose-and-schedule a text-only deferred send for the conversation. The
 * message is pulled from the composer; images can't be scheduled, so when one
 * was staged we warn and send text only. Times are picked in the salon's
 * timezone and stored as UTC (matching ScheduledPanel / ScheduledRow).
 */
export const ScheduleSendModal: React.FC<ScheduleSendModalProps> = ({
  opened,
  onClose,
  conversationId,
  timezone,
  initialBody,
  mediaStripped,
  onScheduled,
}) => {
  const { isMobile } = useResponsive();
  const { createScheduled } = useMessaging();
  const tzAbbr = moment.tz(timezone).format("z");

  const [body, setBody] = useState(initialBody);
  const [choice, setChoice] = useState<WhenChoice>("morning");
  const [customWhen, setCustomWhen] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);

  // Tomorrow's presets in the salon's timezone.
  const presets = useMemo(() => {
    const base = moment.tz(timezone).add(1, "day").startOf("day");
    return {
      morning: base.clone().hour(MORNING_HOUR),
      afternoon: base.clone().hour(AFTERNOON_HOUR),
    };
  }, [timezone]);

  // Seed / reset each time the modal opens.
  useEffect(() => {
    if (!opened) return;
    setBody(initialBody);
    setChoice("morning");
    setCustomWhen(moment.tz(timezone).add(1, "hour").format(WALL));
    setScheduling(false);
  }, [opened, initialBody, timezone]);

  const segments = useMemo(() => computeSmsSegments(body), [body]);

  const trimmed = body.trim();
  const canSchedule =
    trimmed.length > 0 && (choice !== "custom" || Boolean(customWhen));

  const resolveIso = (): string | null => {
    if (choice === "morning") return presets.morning.toISOString();
    if (choice === "afternoon") return presets.afternoon.toISOString();
    return customWhen ? moment.tz(customWhen, timezone).toISOString() : null;
  };

  const handleSchedule = async () => {
    const iso = resolveIso();
    if (!trimmed || !iso) return;
    setScheduling(true);
    try {
      await createScheduled(conversationId, trimmed, iso);
      notifySuccess("Your message will send at the scheduled time.", {
        title: "Message scheduled",
      });
      onScheduled();
      onClose();
    } catch {
      // Keep the modal open so the user can retry; the context surfaces errors.
      setScheduling(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Schedule send</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!scheduling}
      closeOnEscape={!scheduling}
    >
      <BrandedLoadingOverlay visible={scheduling} />

      <Stack gap="md">
        <Stack gap="xs">
          <Group justify="space-between" align="center" gap="xs">
            <Text className={styles.sectionLabel}>When to send</Text>
            <Text size="xs" c="dimmed">
              Times displayed in {tzAbbr}
            </Text>
          </Group>
          <Stack gap="xs" role="radiogroup" aria-label="When to send">
            <PresetRow
              selected={choice === "morning"}
              onSelect={() => setChoice("morning")}
              title="Tomorrow morning"
              caption={presets.morning.format("ddd, MMM D")}
              value={presets.morning.format("h:mm A")}
            />
            <PresetRow
              selected={choice === "afternoon"}
              onSelect={() => setChoice("afternoon")}
              title="Tomorrow afternoon"
              caption={presets.afternoon.format("ddd, MMM D")}
              value={presets.afternoon.format("h:mm A")}
            />
            <PresetRow
              selected={choice === "custom"}
              onSelect={() => setChoice("custom")}
              title="Custom time…"
              value="Choose"
            />
            {choice === "custom" && (
              <DateTimePicker
                value={customWhen}
                onChange={setCustomWhen}
                minDate={moment.tz(timezone).format(WALL)}
                valueFormat="MMM D, YYYY h:mm A"
                timePickerProps={{ format: "12h" }}
                aria-label="Custom send time"
                popoverProps={{ withinPortal: true }}
              />
            )}
          </Stack>
        </Stack>

        <Stack gap="xs">
          <Text className={styles.sectionLabel}>Message</Text>
          <Box>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.currentTarget.value)}
              placeholder="Type the message to schedule…"
              autosize
              minRows={3}
              maxRows={8}
              aria-label="Message to schedule"
            />
            {body.length > 0 && (
              <Text size="xs" c="dimmed" ta="right" mt={4} aria-live="polite">
                {segments.length}/{segments.encoding === "GSM-7" ? 160 : 70} ·{" "}
                {segments.segments} seg{segments.segments === 1 ? "" : "s"}
              </Text>
            )}
          </Box>
          {mediaStripped && (
            <Alert
              icon={<IconPhotoOff size={16} />}
              color="var(--color-warning)"
              variant="light"
            >
              Scheduled messages are text-only. Your attached image won’t be
              included and stays in the composer.
            </Alert>
          )}
        </Stack>

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={scheduling}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSchedule}
            loading={scheduling}
            disabled={!canSchedule}
          >
            Schedule send
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ScheduleSendModal;
